/**
 * LocationPicker — unified smart origin/destination picker (Phase 2, behind the
 * REACT_APP_SMART_PORT_PICKER flag). One component drives ocean + air (UX §8): mode only
 * swaps the data source (seaports vs airports), icon, and grouping.
 *
 * Suggestions are grouped — ocean: PORTS then PLACES; air (Flights-style): CITIES
 * (cityIden groups) / AIRPORTS / PLACES. Picking an exact port/airport → that one; a
 * city group → all its airports (AIR_CITY); a generic place → resolveNearestPorts →
 * tiered nearest candidates with a toggleable expander. Emits the existing single-port
 * `selectedPort` (backward compatible) plus an optional `pickedLocation` + `candidates`
 * the parallel executor consumes. Degrades to a port-only list if Google Places is
 * unavailable (UX §6).
 */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import './LocationPicker.css';
import { setQueryParams } from '../../actions/route_actions';
import {
  PlaceKind,
  ShipmentMode,
  resolveNearestPorts,
  makePickedLocation,
  roundKm,
} from '../../util/portPicker';
import * as places from '../../util/portPicker/placesService';
import { get_all_airports_from_db } from '../../GraphQL/airportsData';

const DEBOUNCE_MS = 250;
const MAX_PORT_SUGGESTIONS = 8;
const ICON = { anchor: '⚓', plane: '✈', pin: '📍', dot: '●' };

// Suggestion-list sizing. LIST_MAX_HEIGHT mirrors the `.lp-list` CSS default so the full
// app is visually unchanged; the other two bound the viewport-fit clamp below.
const LIST_MAX_HEIGHT = 320; // roomy default on a normal (tall) viewport
const LIST_MIN_HEIGHT = 140; // comfort target (~a group header + 3 rows) when there's room for it
const LIST_GAP = 8; // breathing room kept below the list so it never touches the edge

// ── pure view helpers (top of file) ─────────────────────────────────────────────
const portIcon = (mode) => (mode === ShipmentMode.AIR ? ICON.plane : ICON.anchor);
const norm = (s) => (s == null ? '' : String(s).trim().toLowerCase());

/**
 * Clamp the dropdown's max-height to the room actually available below the input in the
 * current viewport. The list is `position:absolute`, so if it can grow past the viewport it
 * grows the document — which is exactly what makes the WordPress embed (a fixed-height
 * iframe) show its own scrollbar when the port list opens. Fitting the list to the viewport
 * makes it scroll INTERNALLY instead, so the iframe never overflows. On the full app the
 * viewport is tall, so this resolves to LIST_MAX_HEIGHT and nothing changes.
 *
 * Not overflowing is the priority: when the space below the input is tighter than the comfort
 * target (`min`) we return the exact available space rather than forcing `min` — otherwise a
 * shorter-than-expected embed would reintroduce the very scrollbar this prevents. `min` only
 * applies when there's actually room for it.
 *
 * @param {number} viewportHeight - window.innerHeight (the iframe's own inner height when embedded)
 * @param {number} inputBottom - the input's viewport-relative bottom edge (getBoundingClientRect().bottom)
 * @returns {number} max-height in px (0 when the input's bottom is already at/below the viewport edge)
 */
export const clampDropdownHeight = (
  viewportHeight,
  inputBottom,
  { max = LIST_MAX_HEIGHT, min = LIST_MIN_HEIGHT, gap = LIST_GAP } = {}
) => {
  const available = Math.floor(viewportHeight - inputBottom - gap);
  if (available <= 0) return 0; // input at/below the edge → collapse rather than overflow
  if (available < min) return available; // tight embed → fit exactly (list still scrolls internally)
  return Math.min(max, available); // room to spare → comfortable, capped at max
};

/**
 * Relevance score for a port/airport against a query (lower = better, -1 = no match).
 * Country matches are first-class — typing "belg" surfaces Belgium's ports/airports,
 * not just a name like "Belgrade" — ranked just under direct name/code prefix hits.
 */
const scoreMatch = (p, q) => {
  const name = norm(p.Name);
  const code = norm(p.portCode);
  const country = norm(p.Country);
  const city = norm(p.city);
  if (name.startsWith(q) || code === q) return 0;
  if (country.startsWith(q)) return 1; // "belg" → Belgium
  if (city.startsWith(q)) return 2;
  if (name.includes(q) || code.includes(q)) return 3;
  if (country.includes(q) || city.includes(q)) return 4;
  return -1;
};

/** Filter + rank ports/airports by query (name / code / country / city); cap the list. */
const filterPorts = (ports, query) => {
  const q = norm(query);
  if (!q) return (ports || []).slice(0, MAX_PORT_SUGGESTIONS);
  const scored = [];
  for (const p of ports || []) {
    const s = scoreMatch(p, q);
    if (s >= 0) scored.push({ p, s });
  }
  scored.sort((a, b) => a.s - b.s || norm(a.p.Name).localeCompare(norm(b.p.Name)));
  return scored.slice(0, MAX_PORT_SUGGESTIONS).map((x) => x.p);
};

/** Air: group airports by cityIden → [{ cityIden, city, airports }], multi-airport cities first. */
const groupByCity = (airports) => {
  const byCity = new Map();
  for (const a of airports) {
    if (a.cityIden == null) continue;
    if (!byCity.has(a.cityIden)) byCity.set(a.cityIden, { cityIden: a.cityIden, city: a.city || a.Name, airports: [] });
    byCity.get(a.cityIden).airports.push(a);
  }
  return [...byCity.values()].filter((g) => g.airports.length >= 2); // only real multi-airport cities
};

/** Persist only the minimum per candidate into queryParams (which gets serialized to the
 *  URL) — id + name + distance, never the full port object. Avoids URL bloat / PII. */
const slimCandidates = (cands) =>
  (cands || []).map((c) => ({ port: { id: c.port.id, Name: c.port.Name }, distanceKm: c.distanceKm }));

/** Map a PF port row → the backward-compatible selectedPort object (CustomDropdown shape). */
const toSelectedPort = (port) => ({
  id: port.id,
  name: port.Name,
  country: port.Country,
  countryId: port.CountryId,
  linkedPort: port.linkedPort,
  portLinked: port.portLinked,
  portCode: port.portCode,
});

class LocationPicker extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      query: '',
      open: false,
      predictions: [],
      loadingPredictions: false, // Google autocomplete request in flight
      activeIndex: -1,
      picked: null, // { selectedPort, pickedLocation }
      candidates: [], // [{ port, distanceKm, tier }]
      excludedIds: {}, // id → true (user-toggled-off nearest ports)
      expanded: true,
      airports: [], // air-mode data source (lazy-loaded, cached)
      airportsLoaded: false,
      listMaxHeight: LIST_MAX_HEIGHT, // shrunk to fit the viewport while open (embed-safe)
    };
    this._debounce = null;
    this._session = null;
    this.inputRef = React.createRef();
  }

  componentDidMount() {
    this.rehydrateFromRedux(); // restore the chip if a selection already lives in redux
    if (this.props.mode === ShipmentMode.AIR) this.loadAirports();
    this.maybeSeed();
    // Re-fit the open list when the viewport moves under it (window resize, or a scroll in
    // any ancestor — capture:true catches the iframe's own scroll too).
    window.addEventListener('resize', this.onViewportChange);
    window.addEventListener('scroll', this.onViewportChange, true);
  }

  /** Re-measure only while the list is open — the listeners are cheap no-ops otherwise. */
  onViewportChange = () => {
    if (this.state.open) this.measureList();
  };

  /**
   * Shrink `listMaxHeight` to the space below the input in the current viewport, so the
   * absolutely-positioned list scrolls internally instead of overflowing the embed iframe
   * (see clampDropdownHeight). No-op until the input is mounted.
   */
  measureList = () => {
    const el = this.inputRef.current;
    if (!el || typeof window === 'undefined') return;
    const { bottom } = el.getBoundingClientRect();
    const next = clampDropdownHeight(window.innerHeight, bottom);
    if (next !== this.state.listMaxHeight) this.setState({ listMaxHeight: next });
  };

  // Controlled reuse (RFQ creator): when `onPick` is supplied, the selection is owned
  // by the parent (via `value`) instead of the global search queryParams, so this
  // picker can drive the RFQ form without touching the search lane.
  get controlled() {
    return typeof this.props.onPick === 'function';
  }

  componentWillUnmount() {
    if (this._debounce) clearTimeout(this._debounce);
    window.removeEventListener('resize', this.onViewportChange);
    window.removeEventListener('scroll', this.onViewportChange, true);
  }

  /**
   * The selection persists in redux (queryParams[side] + [side]Picked); the chip is local
   * state. On (re)mount — HMR, route change, back-nav — rebuild the chip from redux so the
   * selection is never visually lost while it's still in the store.
   */
  /** Synthesize a chip { selectedPort, pickedLocation } from a bare selectedPort
   *  (the CustomDropdown shape). null when there's no real port. */
  buildPickedFromPort(selectedPort) {
    if (!selectedPort?.id) return null;
    const pickedLocation = makePickedLocation({
      kind: this.props.mode === ShipmentMode.AIR ? PlaceKind.AIRPORT : PlaceKind.SEAPORT,
      label: selectedPort.name,
      country: selectedPort.country,
      countryId: selectedPort.countryId,
      portId: selectedPort.id,
      code: selectedPort.portCode,
    });
    return { selectedPort, pickedLocation };
  }

  rehydrateFromRedux() {
    if (this.state.picked) return;
    const { side, queryParams } = this.props;
    // Controlled mode (RFQ creator): the selection lives in the parent's `value`,
    // not redux. Build the chip from it (and skip the queryParams path entirely).
    const selectedPort = this.controlled ? this.props.value : queryParams?.[side];
    if (!selectedPort?.id) return;
    const stored = !this.controlled && queryParams[`${side}Picked`];
    if (stored && stored.pickedLocation) {
      this.setState({ picked: { selectedPort, pickedLocation: stored.pickedLocation }, candidates: stored.candidates || [] });
      return;
    }
    // legacy / partial / controlled: synthesize a chip from the selectedPort alone.
    const picked = this.buildPickedFromPort(selectedPort);
    if (picked) this.setState({ picked, candidates: [] });
  }

  componentDidUpdate(prevProps, prevState) {
    // Fit the list to the viewport the moment it opens (covers focus, typing, and editChip).
    if (this.state.open && !prevState.open) this.measureList();
    if (this.props.mode === ShipmentMode.AIR && prevProps.mode !== ShipmentMode.AIR) this.loadAirports();
    if (this.props.seedQuery !== prevProps.seedQuery) this._seeded = false; // new draft value → re-seed
    // Controlled mode: the parent can set/replace `value` AFTER mount — e.g. an OCEAN
    // draft that eagerly resolves the port object once seaports load (a race with the
    // draft fetch). render() only shows a chip from state.picked, so sync it here when
    // `value` changes to a different port (or clear it when value becomes null). Guard
    // on id so the picker's own commit (which already set state.picked) is a no-op.
    if (this.controlled && this.props.value !== prevProps.value) this.syncControlledValue();
    this.maybeSeed();
  }

  /** Keep state.picked in step with a parent-driven `value` change (controlled mode). */
  syncControlledValue() {
    const value = this.props.value;
    const currentId = this.state.picked?.selectedPort?.id;
    if (value?.id) {
      if (value.id !== currentId) {
        const picked = this.buildPickedFromPort(value);
        if (picked) {
          this._seeded = true; // an external port wins over a pending seed
          this.setState({ picked, candidates: [], query: '', open: false });
        }
      }
    } else if (this.state.picked) {
      this.setState({ picked: null, candidates: [] }); // value cleared by the parent
    }
  }

  /**
   * Controlled prefill (RFQ creator): resolve a parsed origin/dest name (e.g. "Montreal",
   * "Dhaka", "Shanghai") to a real port/airport using THIS picker's own matcher (name /
   * code / country / city), once its data source has loaded. Auto-selects the best match
   * so the chip shows the resolved facility + IATA/port code. Fires once per seed value;
   * a no-match leaves the field empty for the agent to pick manually.
   */
  maybeSeed() {
    if (!this.controlled || this._seeded) return;
    const seed = (this.props.seedQuery || '').trim();
    // A controlled `value` IS an existing selection — treat it as one even before
    // rehydrateFromRedux's setState lands (componentDidMount calls it then maybeSeed
    // synchronously). Without this, a caller passing both `value` and `seedQuery`
    // could seed over the parent's selection on the first tick.
    if (!seed || this.state.picked || this.props.value?.id) return;
    const data = this.activeData;
    if (!data?.length) return; // wait until ports/airports load
    this._seeded = true;
    const match = filterPorts(data, seed)[0];
    if (match) this.selectPort(match);
  }

  /** Map a Company id → its country, from the already-loaded seaport list (memoized). The
   *  Airports model carries no country directly; its `cityIden` is the parent city Company,
   *  which (for most airports) is in the port list — so we can label airports by country. */
  get countryById() {
    if (this._cbidSrc !== this.props.ports) {
      this._cbidSrc = this.props.ports;
      this._cbid = new Map((this.props.ports || []).map((p) => [p.id, p.Country]));
    }
    return this._cbid;
  }

  /** Lazy-load airports for air mode (cached in airportsData; shared across instances).
   *  Enrich each with its country (via cityIden → port country) so country search works. */
  loadAirports() {
    if (this.state.airportsLoaded) return;
    const { airportAuth } = this.props;
    get_all_airports_from_db(airportAuth && airportAuth.jwt, airportAuth && airportAuth.isUserPresent).then((airports) => {
      const byId = this.countryById;
      const withCountry = airports.map((a) => (a.Country ? a : { ...a, Country: byId.get(a.cityIden) || null }));
      this.setState({ airports: withCountry, airportsLoaded: true });
    });
  }

  /** Active data source: airports in air mode, seaports otherwise. */
  get activeData() {
    return this.props.mode === ShipmentMode.AIR ? this.state.airports : this.props.ports;
  }

  // ── data ───────────────────────────────────────────────────────────────────
  get suggestions() {
    const placeRows = this.state.predictions.map((p) => ({ type: 'place', place: p }));
    if (this.props.mode === ShipmentMode.AIR) {
      // Flights-style: city groups (cityIden) first, then individual airports, then places.
      const matches = filterPorts(this.state.airports, this.state.query);
      const cities = groupByCity(matches).map((group) => ({ type: 'city', group }));
      const airports = matches.map((port) => ({ type: 'airport', port }));
      return [...cities, ...airports, ...placeRows];
    }
    const ports = filterPorts(this.props.ports, this.state.query).map((port) => ({ type: 'port', port }));
    return [...ports, ...placeRows]; // PF ports rank above places (UX §2)
  }

  // ── input + Google predictions (debounced) ───────────────────────────────────
  onInputChange = (e) => {
    const query = e.target.value;
    this.setState({ query, open: true, activeIndex: -1 });
    if (this._debounce) clearTimeout(this._debounce);
    // Don't gate on isAvailable() here — getPredictions lazily loads the SDK and resolves []
    // if it's truly unavailable (no API key), so address search works as soon as it loads.
    if (!query.trim()) {
      this.setState({ predictions: [], loadingPredictions: false });
      return;
    }
    if (!this._session) this._session = places.newSessionToken();
    this.setState({ loadingPredictions: true });
    this._debounce = setTimeout(() => {
      const inflight = query;
      places.getPredictions(query, this._session).then((predictions) => {
        // ignore a stale response if the user kept typing
        if (this.state.query === inflight) this.setState({ predictions, loadingPredictions: false });
      });
    }, DEBOUNCE_MS);
  };

  // ── selection ────────────────────────────────────────────────────────────────
  selectPort = (port) => {
    const kind = this.props.mode === ShipmentMode.AIR ? PlaceKind.AIRPORT : PlaceKind.SEAPORT;
    const pickedLocation = makePickedLocation({
      kind,
      label: port.Name,
      lat: port.latitude,
      lng: port.longitude,
      country: port.Country,
      countryId: port.CountryId,
      portId: port.id,
      code: port.portCode,
    });
    this.commit({
      picked: { selectedPort: toSelectedPort(port), pickedLocation },
      candidates: [{ port, distanceKm: 0, tier: 0 }],
    });
  };

  selectPlace = async (place) => {
    const details = await places.getPlaceDetails(place.placeId, this._session);
    this._session = null; // session ends at details fetch
    if (!details) return; // keep list open; user can pick a port/airport instead
    const candidates = resolveNearestPorts(
      { lat: details.lat, lng: details.lng, country: details.country, countryId: details.countryId },
      this.activeData // seaports (ocean) or airports (air)
    );
    const pickedLocation = makePickedLocation({
      kind: PlaceKind.GEO_PLACE,
      label: place.label || details.label,
      lat: details.lat,
      lng: details.lng,
      country: details.country,
      countryId: details.countryId,
      placeId: place.placeId,
    });
    const primary = candidates[0];
    this.commit({
      picked: { selectedPort: primary ? toSelectedPort(primary.port) : null, pickedLocation },
      candidates,
    });
  };

  /** Air city group: search all airports sharing that cityIden (AIR_CITY). */
  selectCity = (group) => {
    const candidates = group.airports.map((port) => ({ port, distanceKm: 0, tier: 0 }));
    const primary = group.airports[0];
    const pickedLocation = makePickedLocation({
      kind: PlaceKind.AIR_CITY,
      label: `${group.city} · all airports`,
      lat: primary.latitude,
      lng: primary.longitude,
    });
    this.commit({ picked: { selectedPort: toSelectedPort(primary), pickedLocation }, candidates });
  };

  /** Persist the pick: backward-compatible selectedPort + optional picked/candidates.
   *  SET_QUERY_PARAMS replaces the whole object, so spread current params. Never clobber an
   *  existing selectedPort with null (a place that resolved to no nearby port keeps the chip
   *  but leaves the prior port intact so getJSON's guard still holds). */
  commit({ picked, candidates }) {
    const { side } = this.props;
    // Controlled (RFQ creator): the selection must be a real port/airport — it rides to
    // C as the lane port and drives the drayage estimate. Mirror the uncontrolled guard
    // below: a place that resolved to NO nearby port is not a usable selection, so leave
    // the parent's value (and the current chip) untouched rather than clobbering with
    // null. Picks WITH a resolved port (incl. a place whose nearest port is primary)
    // commit normally.
    if (this.controlled) {
      if (!picked.selectedPort) return;
      this.setState({ picked, candidates, excludedIds: {}, open: false, expanded: true, query: '' });
      this.props.onPick(picked.selectedPort, picked.pickedLocation, candidates);
      return;
    }
    this.setState({ picked, candidates, excludedIds: {}, open: false, expanded: true, query: '' });
    const next = {
      ...this.props.queryParams,
      [`${side}Picked`]: { pickedLocation: picked.pickedLocation, candidates: slimCandidates(candidates) },
    };
    if (picked.selectedPort) next[side] = picked.selectedPort; // only write a real port
    this.props.setQueryParams(next);
  }

  toggleCandidate = (id) => {
    this.setState((s) => {
      const excludedIds = { ...s.excludedIds };
      const activeCount = s.candidates.filter((c) => !excludedIds[c.port.id]).length;
      if (excludedIds[id]) delete excludedIds[id];
      else if (activeCount > 1) excludedIds[id] = true; // keep ≥1
      return { excludedIds };
    }, this.syncCandidates);
  };

  activeCandidateCount() {
    return this.state.candidates.filter((c) => !this.state.excludedIds[c.port.id]).length;
  }

  /** Re-emit candidate selection (without the excluded) so the executor honours toggles. */
  syncCandidates = () => {
    const { side } = this.props;
    const active = this.state.candidates.filter((c) => !this.state.excludedIds[c.port.id]);
    const port = active[0] ? toSelectedPort(active[0].port) : this.state.picked.selectedPort;
    if (this.controlled) {
      this.props.onPick(port || null, this.state.picked.pickedLocation, active);
      return;
    }
    this.props.setQueryParams({
      ...this.props.queryParams,
      [side]: port,
      [`${side}Picked`]: { pickedLocation: this.state.picked.pickedLocation, candidates: slimCandidates(active) },
    });
  };

  clear = () => {
    this.setState({ picked: null, candidates: [], query: '', predictions: [], open: false });
    this._seeded = true; // a manual clear must not re-trigger the draft seed
    if (this.controlled) {
      this.props.onPick(null, null, []);
      return;
    }
    this.props.setQueryParams({ ...this.props.queryParams, [this.props.side]: null, [`${this.props.side}Picked`]: null });
  };

  /** Click the chip → re-open the input to change the port (no need to clear first). The
   *  redux selection stays until a new pick or a real clear, so cancelling restores it. */
  editChip = () => {
    this.setState({ picked: null, query: '', predictions: [], activeIndex: -1, open: true }, () => {
      if (this.inputRef.current) this.inputRef.current.focus();
    });
  };

  /** On blur: if the user re-opened a chip but didn't pick anything, restore it from redux. */
  onInputBlur = () => {
    setTimeout(() => {
      if (!this.state.picked) this.rehydrateFromRedux();
      this.setState({ open: false });
    }, 150);
  };

  // ── keyboard ─────────────────────────────────────────────────────────────────
  onKeyDown = (e) => {
    const items = this.suggestions;
    if (!items.length) { if (e.key === 'Escape') this.setState({ open: false }); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); this.setState((s) => ({ open: true, activeIndex: Math.min(s.activeIndex + 1, items.length - 1) })); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); this.setState((s) => ({ activeIndex: Math.max(s.activeIndex - 1, 0) })); }
    else if (e.key === 'Enter') { const it = items[this.state.activeIndex]; if (it) { e.preventDefault(); this.pick(it); } }
    else if (e.key === 'Escape') { this.setState({ open: false }); }
  };

  pick = (item) => {
    if (item.type === 'place') return this.selectPlace(item.place);
    if (item.type === 'city') return this.selectCity(item.group);
    return this.selectPort(item.port); // 'port' (ocean) or 'airport' (air individual)
  };

  // ── render ─────────────────────────────────────────────────────────────────
  renderChip() {
    const { picked, candidates } = this.state;
    const { mode } = this.props;
    const loc = picked.pickedLocation;
    const isPlace = loc.kind === PlaceKind.GEO_PLACE;
    const icon = isPlace ? ICON.pin : portIcon(mode);
    const label = isPlace ? loc.label : `${loc.label}${loc.code ? ` (${loc.code})` : ''}`;
    return (
      <div className="lp-chip-wrap">
        <span className="lp-chip" data-testid="lp-chip" role="button" tabIndex={0} title="Click to change"
          onClick={this.editChip}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.editChip(); } }}>
          <span aria-hidden="true">{icon}</span>
          <span className="lp-chip-label">{label}</span>
          <button type="button" className="lp-clear" aria-label="Clear selection"
            onClick={(e) => { e.stopPropagation(); this.clear(); }}>✕</button>
        </span>
        {isPlace && candidates.length > 0 && this.renderNearest()}
      </div>
    );
  }

  renderNearest() {
    const { candidates, excludedIds, expanded } = this.state;
    const count = this.activeCandidateCount();
    const kind = this.props.mode === ShipmentMode.AIR ? 'airport' : 'port';
    const rowIcon = portIcon(this.props.mode);
    return (
      <div className="lp-nearest">
        <button type="button" className="lp-nearest-toggle" aria-expanded={expanded}
          onClick={() => this.setState((s) => ({ expanded: !s.expanded }))}>
          <span aria-hidden="true">{expanded ? '▴' : '▾'}</span>
          Quoting the {count} nearest {kind}{count === 1 ? '' : 's'}
        </button>
        {expanded && (
          <ul className="lp-nearest-list">
            {candidates.map((c) => {
              const off = !!excludedIds[c.port.id];
              return (
                <li key={c.port.id} className={off ? 'lp-cand off' : 'lp-cand'}>
                  <label>
                    <input type="checkbox" checked={!off} onChange={() => this.toggleCandidate(c.port.id)} />
                    <span className="lp-cand-icon" aria-hidden="true">{rowIcon}</span>
                    <span className="lp-cand-name">{c.port.Name}</span>
                    <span className="lp-km">{c.distanceKm == null ? '—' : `${roundKm(c.distanceKm)} km`}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }

  renderRow(item, idx) {
    const active = idx === this.state.activeIndex;
    const cls = active ? 'lp-opt active' : 'lp-opt';
    const optId = `lp-list-${this.props.side}-opt-${idx}`; // matches aria-activedescendant
    if (item.type === 'city') {
      const g = item.group;
      return (
        <li key={`c${g.cityIden}`} id={optId} role="option" aria-selected={active} className={cls}
          onMouseDown={(e) => { e.preventDefault(); this.selectCity(g); }}>
          <span aria-hidden="true">{ICON.plane}</span>
          <span className="lp-primary">{g.city} — all airports</span>
          <span className="lp-secondary">({g.airports.length})</span>
        </li>
      );
    }
    if (item.type === 'port' || item.type === 'airport') {
      const p = item.port;
      const secondary = item.type === 'airport' ? p.city : p.Country;
      return (
        <li key={`p${p.id}`} id={optId} role="option" aria-selected={active} className={cls}
          onMouseDown={(e) => { e.preventDefault(); this.selectPort(p); }}>
          <span aria-hidden="true">{portIcon(this.props.mode)}</span>
          <span className="lp-primary">{p.Name}</span>
          <span className="lp-secondary">{secondary || ''}</span>
          <span className="lp-code">{p.portCode || ''}</span>
        </li>
      );
    }
    const pl = item.place;
    return (
      <li key={`g${pl.placeId}`} id={optId} role="option" aria-selected={active} className={cls}
        onMouseDown={(e) => { e.preventDefault(); this.selectPlace(pl); }}>
        <span aria-hidden="true">{ICON.pin}</span>
        <span className="lp-primary">{pl.primary}</span>
        <span className="lp-secondary">{pl.secondary}</span>
      </li>
    );
  }

  render() {
    if (this.state.picked) return this.renderChip();
    const items = this.suggestions;
    const { mode, side } = this.props;
    const isAir = mode === ShipmentMode.AIR;
    const cityRows = items.filter((i) => i.type === 'city');
    const portRows = items.filter((i) => i.type === 'port' || i.type === 'airport');
    const placeRows = items.filter((i) => i.type === 'place');
    const listId = `lp-list-${side}`;
    const typed = this.state.query.trim().length > 0;
    const dataLoading = isAir ? !this.state.airportsLoaded : (this.props.ports || []).length === 0;
    const loading = this.state.loadingPredictions || (dataLoading && typed);
    const noMatches = this.state.open && typed && !loading && items.length === 0;
    const loadingLabel = dataLoading ? (isAir ? 'Loading airports…' : 'Loading ports…') : 'Searching…';
    const activeId = this.state.activeIndex >= 0 ? `${listId}-opt-${this.state.activeIndex}` : undefined;
    return (
      <div className="lp-root">
        {/* ARIA 1.2 combobox: role + state live on the focusable input, not the wrapper */}
        <input
          ref={this.inputRef}
          className="lp-input"
          type="text"
          autoComplete="off"
          role="combobox"
          aria-expanded={this.state.open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          placeholder={side === 'from' ? 'Origin — city, port, or address' : 'Destination — city, port, or address'}
          value={this.state.query}
          onChange={this.onInputChange}
          onFocus={() => this.setState({ open: true })}
          onBlur={this.onInputBlur}
          onKeyDown={this.onKeyDown}
          aria-label={side === 'from' ? 'Origin location' : 'Destination location'}
        />
        {this.state.open && (items.length > 0 || loading || noMatches) && (
          <ul className="lp-list" role="listbox" id={listId} style={{ maxHeight: this.state.listMaxHeight }}>
            {loading && (
              <li className="lp-loading" aria-live="polite">
                <span className="lp-spinner" aria-hidden="true" />
                {loadingLabel}
              </li>
            )}
            {cityRows.length > 0 && <li className="lp-group" aria-hidden="true">CITIES</li>}
            {cityRows.map((it) => this.renderRow(it, items.indexOf(it)))}
            {portRows.length > 0 && <li className="lp-group" aria-hidden="true">{isAir ? 'AIRPORTS' : 'PORTS'}</li>}
            {portRows.map((it) => this.renderRow(it, items.indexOf(it)))}
            {placeRows.length > 0 && <li className="lp-group" aria-hidden="true">PLACES</li>}
            {placeRows.map((it) => this.renderRow(it, items.indexOf(it)))}
            {noMatches && <li className="lp-empty">No matching {isAir ? 'airports' : 'ports'} or places</li>}
          </ul>
        )}
      </div>
    );
  }
}

LocationPicker.propTypes = {
  mode: PropTypes.string, // 'ocean' | 'air'
  side: PropTypes.string, // 'from' | 'to'
  ports: PropTypes.array, // seaport list (redux)
  airportAuth: PropTypes.shape({ jwt: PropTypes.string, isUserPresent: PropTypes.bool }),
  queryParams: PropTypes.object,
  setQueryParams: PropTypes.func,
  // Controlled reuse (RFQ creator): when onPick is set, the selection is owned by the
  // parent via `value` (a selectedPort) instead of the global search lane, and `seedQuery`
  // (a parsed origin/dest name) is auto-resolved against this picker's own matcher.
  value: PropTypes.object,
  onPick: PropTypes.func,
  seedQuery: PropTypes.string,
};

const portsForSide = (state, side) => {
  const json = side === 'from' ? state.route.originPortJSON : state.route.destinationPortJSON;
  return (json && json.Ports) || [];
};

// Token + identity for the air-mode airports fetch (mirrors get_all_ports_data's choice).
const airportAuthFromState = (state) => {
  const r = state.route || {};
  const ud = r.USER_DETAILS;
  const isUserPresent = ud != null && (typeof ud === 'object' ? Object.keys(ud).length > 0 : String(ud).trim().length > 0);
  const jwt = isUserPresent ? r.CLIENT_TOKEN && r.CLIENT_TOKEN.JWT_TOKEN : r.JWT_TOKEN && r.JWT_TOKEN.JWT_TOKEN;
  return { jwt: jwt || '', isUserPresent };
};

const mapStateToProps = (state, ownProps) => ({
  ports: portsForSide(state, ownProps.side),
  airportAuth: airportAuthFromState(state),
  queryParams: state.route.queryParams || {},
  primaryColor: state.code_config ? state.code_config.primaryColor : undefined,
});

export default connect(mapStateToProps, { setQueryParams })(LocationPicker);
